/**
 * MarkInvoicePaymentModal — passo 2 para UMA fatura: dizer que um pagamento já recebido a paga.
 *
 * Substitui o `InvoicePaymentModal` na tela de Títulos. A diferença estrutural não é de campo, é
 * de ato: lá o usuário CRIAVA o pagamento e liquidava a fatura na mesma submissão; aqui ele
 * ESCOLHE um pagamento que já entrou e apenas o aponta para a fatura. A liquidação depende da
 * aprovação do financeiro (passo 3).
 *
 * O que sai: data, tipo de pagamento, pagador, comprovante — tudo isso já foi informado no passo 1
 * e mostrar de novo convidaria a divergir do que está gravado.
 *
 * O que fica: o valor (quanto DESTE pagamento vai para esta fatura), as observações e as opções de
 * "fatura nova" — que aqui são uma PROMESSA, cumprida só na aprovação.
 *
 * O modo (Liquidar / Baixa Parcial / Amortizar) continua vindo do item de menu, como antes: ter
 * botão e campo permitia abrir "Liquidar" e submeter uma parcial, com o rótulo mentindo.
 */
import { useEffect, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import PendingPaymentPicker, { availableOf } from '../../../molecules/payment/PendingPaymentPicker';
import CurrencyInput from '../../../molecules/CurrencyInput';
import PaymentInfoModal from './PaymentInfoModal';
import HintBadge from '../../../molecules/payment/HintBadge';
import { amountHint } from '../../../molecules/payment/paymentHints';
import {
    InvoicePaymentActionSettings, MarkInvoiceItem, PaymentActionPromise, PaymentRow, ResidualInvoiceSetup,
    getInvoicePaymentActionSettings, listPaymentSources, markInvoices,
} from '../services/billingApi';
import { effectiveResidualInvoices } from '../services/residualInvoices';
import {
    effectiveChoice, markingField, previousInvoiceOutcome, resolutionFor,
} from '../services/invoicePaymentAction';
import ResidualInvoicesField from './ResidualInvoicesField';
import {
    PaymentSourceOption, allowsGuarantorRefund, defaultPaymentSource,
} from '../services/paymentSources';
import { formatBRL } from '../../../../utils/currencyUtils';
import { PAYMENT_SOURCE_HIGHLIGHT } from '../../../../constants/payment';
import { formatDateBR } from '../../../../utils/DateUtils';
import {
    LiquidationMode, MODE_TO_TOTAL_PAYMENT, MODE_LABELS, WATERFALL_HINT,
    PAYMENT_MODES_BY_SCENARIO, clampMode,
} from '../constants/liquidationModes';
import NotesList from '../../../molecules/NotesList';

export type PaymentMode = LiquidationMode;

/** Rótulos por modo. O botão diz "Sinalizar", não "Liquidar": não é isso que a ação faz. */
const MODE_UI: Record<LiquidationMode, { heading: string; submittingLabel: string }> = {
    TOTAL: { heading: 'Sinalizar pagamento — liquidar título', submittingLabel: 'Sinalizando...' },
    PARTIAL: { heading: 'Sinalizar pagamento — baixa parcial', submittingLabel: 'Sinalizando...' },
    OPERATION_WATERFALL: { heading: 'Sinalizar pagamento — amortizar operação', submittingLabel: 'Sinalizando...' },
};

const POST_SETTLEMENT_UI = {
    heading: 'Sinalizar pagamento — pós liquidação',
    submittingLabel: 'Sinalizando...',
};

type ReplaceChoice = 'CANCEL' | 'UNCOLLECT' | 'KEEP';

/**
 * O que fazer com a fatura anterior. Mesmas três escolhas do fluxo antigo, com o efeito DIFERIDO:
 * aqui só se registra a promessa.
 *
 * Escopo único (sem `_ALL`) porque o usuário escolheu UMA fatura no menu — aplicar no título
 * inteiro cancelaria cobrança que ninguém pediu para cancelar. Quem pede escopo de título é a
 * Identificação de Pagamento, onde a linha É o título.
 */
const PROMISE_BY_CHOICE: Record<ReplaceChoice, PaymentActionPromise> = {
    CANCEL: 'CANCEL',
    UNCOLLECT: 'UNCOLLECT',
    KEEP: 'NOTHING',
};

const REPLACE_CHOICES: { value: ReplaceChoice; label: string }[] = [
    { value: 'CANCEL', label: 'Cancelar' },
    { value: 'UNCOLLECT', label: 'Só deixar de cobrar' },
    { value: 'KEEP', label: 'Manter como está' },
];

/**
 * O aviso da fatura nova. Com a escolha nesta tela, as três opções; sem ela (`outcome`, AF-4062), o
 * que vai acontecer com a anterior — o padrão oculto, a decisão do aprovador ou a cascata do baldinho.
 */
const ReplaceActionNotice: React.FC<{
    value: ReplaceChoice | '';
    onChange: (choice: ReplaceChoice) => void;
    outcome?: string;
}> = ({ value, onChange, outcome }) => outcome ? (
    <div className="px-3 py-2 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
        <p className="font-medium text-amber-800 dark:text-amber-300">
            Esta sinalização vai criar uma fatura nova
        </p>
        <p className="text-amber-700 dark:text-amber-400 mt-0.5">
            A fatura é imutável quanto à cobrança: valor diferente do faturado
            ou boleto envolvido exigem uma fatura nova.
        </p>
        <p className="text-amber-800 dark:text-amber-300 mt-2">{outcome}</p>
    </div>
) : (
    <div className="px-3 py-2 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
        <p className="font-medium text-amber-800 dark:text-amber-300">
            Esta sinalização vai criar uma fatura nova
        </p>
        <p className="text-amber-700 dark:text-amber-400 mt-0.5">
            A fatura é imutável quanto à cobrança: valor diferente do faturado
            ou boleto envolvido exigem uma fatura nova. O que fazer com esta
            — <strong>quando o pagamento for aprovado</strong>:
        </p>
        <div className="flex gap-4 mt-2">
            {REPLACE_CHOICES.map(choice => (
                <label key={choice.value} className="inline-flex items-center gap-1">
                    <input type="radio" name="promessa" value={choice.value}
                        checked={value === choice.value}
                        onChange={() => onChange(choice.value)} />
                    {choice.label}
                </label>
            ))}
        </div>
        <p className="text-amber-700 dark:text-amber-400 mt-2">
            {value === 'KEEP'
                ? 'A fatura anterior continua em aberto e sendo cobrada, ao lado da nova.'
                : 'Nada acontece com ela agora: o efeito é aplicado na aprovação.'}
        </p>
    </div>
);

const LABEL = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const INPUT = 'w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white';
const SOURCE_SELECT = `w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:text-white ${PAYMENT_SOURCE_HIGHLIGHT}`;

interface Props {
    invoice: any;
    charge: any;
    mode: PaymentMode;
    postSettlement?: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const MarkInvoicePaymentModal: React.FC<Props> = ({
    invoice, charge, mode, postSettlement, onClose, onSuccess,
}) => {
    const scenario: string = charge.chargeScenario || 'GUARANTEE';
    const allowedModes = PAYMENT_MODES_BY_SCENARIO[scenario] || ['TOTAL'];
    const liquidation = clampMode(mode, allowedModes);
    const ui = postSettlement ? POST_SETTLEMENT_UI : MODE_UI[liquidation];

    // Mesma referência do fluxo antigo: em Liquidar é o saldo do TÍTULO (liquidar é afirmação sobre
    // a DÍVIDA), nos outros modos é o da fatura, onde se paga parte daquela cobrança.
    const titleRemaining = Math.max(
        0, Number(charge.amount || 0) - Number(charge.paidAmount || 0));
    const invoiceRemaining = Number(invoice.amount || 0) - Number(invoice.amountPaid || 0);
    const expected = liquidation === 'TOTAL' ? titleRemaining : invoiceRemaining;

    const [payment, setPayment] = useState<PaymentRow | null>(null);
    const [infoPayment, setInfoPayment] = useState<PaymentRow | null>(null);
    const [notes, setNotes] = useState('');
    /**
     * A escolha do USUÁRIO; `''` enquanto ele não mexeu, e aí vale o padrão da combinação em
     * `INVOICE_PAYMENT_ACTION` (AF-4062). Sem padrão, o campo nasce vazio e o envio fica bloqueado
     * até alguém escolher — ver `podeEnviar`.
     */
    const [replaceAction, setReplaceAction] = useState<ReplaceChoice | ''>('');
    const [submitting, setSubmitting] = useState(false);

    // ── Atributos da COBRANÇA (AF-3671 step 2) ─────────────────────────────────
    // Saíram do registro do pagamento e vieram para cá: descrevem a cobrança, e é aqui que se sabe
    // QUAL cobrança o dinheiro paga. Os rótulos e as regras vêm de business_properties.
    const [sources, setSources] = useState<PaymentSourceOption[]>([]);
    const [paymentSource, setPaymentSource] = useState('');
    const [guarantorRefund, setGuarantorRefund] = useState(false);

    useEffect(() => {
        let alive = true;
        listPaymentSources()
            .then(list => {
                if (!alive) return;
                setSources(list);
                // pré-seleciona pela NATUREZA do título — a mesma regra do `default_if` do seed
                setPaymentSource(prev => prev || defaultPaymentSource(list, scenario));
            })
            .catch(() => { /* sem catálogo o select fica vazio e o usuário escolhe nada */ });
        return () => { alive = false; };
    }, [scenario]);

    const refundAllowed = allowsGuarantorRefund(sources, paymentSource);

    // ── Fatura anterior e residuais (AF-4062, AF-4097) ─────────────────────────
    // `null` = o usuário ainda não mexeu: até lá a lista é derivada do saldo e do modo.
    const [actionSettings, setActionSettings] = useState<InvoicePaymentActionSettings | null>(null);
    const [residualConfigured, setResidualConfigured] = useState<ResidualInvoiceSetup[] | null>(null);

    useEffect(() => {
        let alive = true;
        getInvoicePaymentActionSettings()
            .then(settings => { if (alive) setActionSettings(settings); })
            .catch(() => { /* sem a configuração vale a tela de antes: escolha aqui, sem padrão */ });
        return () => { alive = false; };
    }, []);

    /**
     * O que ainda sobra deste pagamento para marcar.
     *
     * ⚠️ PENDENTE não quer dizer "intocado": um pagamento de 1.000 com 600 já marcados em outro
     * título continua PENDENTE (a conta não fechou) e aparece na lista. É contra ESTE valor que a
     * marcação daqui é medida, não contra o valor cheio.
     */
    const available = payment ? availableOf(payment) : 0;

    /**
     * Sugestão ao escolher o pagamento: o que a fatura espera, limitado ao que sobra.
     *
     * O caso comum é o pagamento cobrir a fatura inteira, e aí digitar seria trabalho repetido. Não
     * cobrindo, o teto é a resposta certa — oferecer o valor da fatura levaria a uma recusa do
     * servidor depois de todo o resto preenchido.
     *
     * Só na TROCA de pagamento, e não a cada render: sobrescrever depois apagaria o que o usuário
     * digitou.
     */
    useEffect(() => {
        if (!payment) return setValue(0);
        setValue(Math.min(expected > 0 ? expected : availableOf(payment), availableOf(payment)));
        // `expected` é derivado do título/fatura, que não mudam enquanto o modal está aberto
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payment?.paymentId]);

    /**
     * Quanto DESTE pagamento vai para esta fatura.
     *
     * ⚠️ Já foi somente-leitura, travado no valor inteiro do pagamento. Fazia sentido enquanto a
     * marcação era tudo-ou-nada: um pagamento só podia ser apontado para um conjunto de faturas que
     * fechasse a conta, então deixar digitar convidava a inventar um número. Com a marcação parcial
     * (AF-3671 step 4) isso inverteu — um pagamento de 10.000 pode pagar uma fatura de 3.000 e
     * seguir pendente pelo resto, e travar o campo obrigaria o usuário a procurar um pagamento de
     * valor exato que talvez não exista.
     *
     * O que continua valendo: a marcação não CRIA dinheiro. Por isso o teto é o que sobra do
     * pagamento (`available`), e não o valor da fatura — pagar menos é desconto e pagar mais é
     * acréscimo, os dois legítimos enquanto couberem no que entrou.
     */
    const [value, setValue] = useState(0);
    const totalPayment = MODE_TO_TOTAL_PAYMENT[liquidation];

    /**
     * Desconto/acréscimo em relação ao esperado, como no fluxo antigo. Só em LIQUIDAÇÃO: numa
     * baixa parcial o valor é naturalmente menor que o esperado, sem ser desconto — chamar aquilo
     * de desconto ensinaria o usuário a ignorar o selo.
     */
    const valueHint = useMemo(
        () => (liquidation === 'TOTAL' && payment ? amountHint(value, expected) : null),
        [liquidation, payment, value, expected]);

    /**
     * Espelha a regra `use_original_invoice` do domínio. Serve para AVISAR: quando não dá para
     * reusar a fatura, o backend deriva uma nova — e é só nesse caso que existe "fatura anterior"
     * para a promessa agir.
     *
     * O critério é o que muda a COBRANÇA: valor diferente do faturado ou boleto envolvido. O modo
     * de liquidação NÃO entra — ele é instrução de liquidação, não termo de cobrança.
     */
    const reusesOriginal = useMemo(() => (
        value === Number(invoice.amount)
        && !invoice.boleto
        && payment?.paymentMethod !== 'BOLETO'
    ), [value, invoice.amount, invoice.boleto, payment]);

    // a combinação muda com a fonte e a liquidação: campo, padrão e residual acompanham
    const resolution = resolutionFor(actionSettings, scenario, paymentSource, totalPayment);
    const field = markingField(resolution);
    const promiseShown = field.shown && !reusesOriginal;
    const chosen = effectiveChoice(replaceAction, resolution);
    const missingChoice = promiseShown && field.required && !chosen;

    // só no parcial que cria fatura nova: total e baldinho não deixam saldo, e a fatura
    // reaproveitada não deriva nada (REGISTER_INVOICE_PAYMENT)
    const residualShown = resolution.residual.visible && totalPayment === false
        && !reusesOriginal && value > 0;
    const residualBalance = Math.round((titleRemaining - value) * 100) / 100;
    // o modal não declara fração: o parcial cobre metade da anterior (`derived_installments`)
    const partialDenominator = Math.max(1, Number(invoice.details?.totalInstallments) || 1) * 2;
    const residualInvoices = residualShown
        ? effectiveResidualInvoices(resolution.residual, residualConfigured, invoice, residualBalance)
        : undefined;
    // sem a escolha nesta tela, o aviso diz o que vai acontecer — menos no reaproveitamento, que não
    // deixa fatura anterior (REGISTER_INVOICE_PAYMENT)
    const replaceNotice = (
        <ReplaceActionNotice value={chosen} onChange={setReplaceAction}
            outcome={promiseShown ? undefined : previousInvoiceOutcome(resolution, totalPayment)} />
    );

    const partiallyMarked = !!payment && (payment.markedAmount || 0) > 0;
    const exceedsPayment = !!payment && value > available;

    // a escolha entra na conta só onde ela é desta tela e não tem padrão: oculta, ou deixada para
    // a aprovação, quem decide é o backend (padrão) ou o aprovador
    const podeEnviar = !!payment && value > 0 && !exceedsPayment && !submitting && !missingChoice;

    const handleSubmit = async () => {
        if (!payment) return toast.error('Selecione o pagamento que paga esta fatura');
        if (!value || value <= 0) return toast.error('Informe um valor maior que zero');
        if (exceedsPayment) {
            return toast.error(
                `O valor passa do que sobra neste pagamento (${formatBRL(available)})`);
        }
        if (missingChoice) {
            return toast.error('Escolha o que fazer com esta fatura quando o pagamento for aprovado');
        }

        // Sem fatura anterior não há alvo para a promessa de escopo único, e o servidor recusa — a
        // tela manda NOTHING. Campo oculto ou vazio: a promessa não vai, e o backend resolve pela
        // combinação (o padrão, ou nula para o aprovador) com o escopo desta tela.
        const promise: PaymentActionPromise | undefined = reusesOriginal
            ? 'NOTHING'
            : promiseShown && chosen ? PROMISE_BY_CHOICE[chosen] : undefined;
        const item: MarkInvoiceItem = {
            invoice_id: invoice.invoiceId,
            value,
            ...(promise ? { payment_action_promise: promise } : {}),
            payment_action_scope: 'INVOICE',
            total_payment: totalPayment,
            payment_source: paymentSource || undefined,
            // só viaja quando a fonte admite: marcar aval de um pagamento do sacado não significa
            // nada, e o backend guardaria um `true` que ninguém sabe interpretar
            pending_guarantor_refund: refundAllowed ? guarantorRefund : false,
            notes: notes || undefined,
            ...(residualInvoices !== undefined ? { residual_invoices: residualInvoices } : {}),
        };

        setSubmitting(true);
        try {
            await markInvoices(payment.paymentId, [item]);
            toast.success('Pagamento sinalizado. Aguardando aprovação do financeiro.');
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(`Não foi possível sinalizar: ${e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-7xl max-h-[92vh] flex flex-col">
                    <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                        <div>
                            <h3 className="text-lg font-semibold">{ui.heading}</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {charge.contractLabel || charge.contractNumber} · fatura{' '}
                                {String(invoice.invoiceId).slice(0, 8)} · valor {formatBRL(invoice.amount)}
                                {invoice.dueDate && <> · vence {formatDateBR(invoice.dueDate)}</>}
                            </p>
                        </div>
                        <button onClick={onClose} aria-label="Fechar"
                            className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                    </div>

                    <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
                        {/* O que a ação faz, dito antes de tudo: quem vem do fluxo antigo espera
                            que este botão pague a fatura, e ele não paga. */}
                        <div className="px-3 py-2 text-sm bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                            <p className="font-medium text-blue-900 dark:text-blue-200">
                                Isto não paga a fatura — sinaliza qual pagamento a paga.
                            </p>
                            <p className="text-blue-800 dark:text-blue-300 text-xs mt-0.5">
                                A fatura continua aberta até o financeiro aprovar a identificação, na
                                tela de <strong>Pagamentos</strong>. Só então ela é liquidada e o
                                título é movimentado.
                            </p>
                        </div>

                        <p className="text-sm px-3 py-2 bg-gray-100 dark:bg-slate-700 rounded">
                            Efeito no título quando aprovado:{' '}
                            <strong>{postSettlement
                                ? 'Recebimento a maior (título já liquidado)'
                                : MODE_LABELS[liquidation]}</strong>
                            {postSettlement && (
                                <span className="block text-xs text-gray-500 mt-0.5">
                                    O título já está liquidado: este valor entra como recebimento a
                                    maior, sem reabrir a dívida.
                                </span>
                            )}
                            {!postSettlement && liquidation === 'OPERATION_WATERFALL' && (
                                <span className="block text-xs text-gray-500 mt-0.5">{WATERFALL_HINT}</span>
                            )}
                            {scenario === 'GUARANTEE' && (
                                <span className="block text-xs text-gray-500 mt-0.5">
                                    Recebível de garantia: o efeito acima é no título de{' '}
                                    <strong>garantia</strong>. O valor é <strong>também</strong>{' '}
                                    distribuído pelas parcelas da operação, nos dois modos.
                                </span>
                            )}
                        </p>

                        <div>
                            <label className={LABEL}>Pagamento que paga esta fatura *</label>
                            <PendingPaymentPicker
                                selectedId={payment?.paymentId}
                                onSelect={setPayment}
                                onInfo={setInfoPayment}
                                exactAmount={Number(invoice.amount || 0)}
                                historyZIndexClass="z-[70]"
                            />
                        </div>

                        <div>
                            {/* O rótulo vem do próprio CurrencyInput: dois `<label>` para o mesmo
                                id deixam o leitor de tela anunciar o campo duas vezes com nomes
                                diferentes. O campo só fica ativo depois de haver pagamento —
                                digitar um valor antes de escolher de ONDE ele sai é inventar
                                dinheiro, que é justamente o que esta tela não faz. */}
                            <CurrencyInput
                                id="valor-sinalizado"
                                label="Valor deste pagamento para esta fatura"
                                prefix="R$"
                                fractionDigits={2}
                                value={value}
                                onChange={setValue}
                                disabled={!payment}
                                className="mb-0"
                            />
                            {payment && (
                                <p className={`text-xs mt-1 ${exceedsPayment ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                    {exceedsPayment
                                        ? `Passa do que sobra neste pagamento: ${formatBRL(available)}`
                                        : `Disponível neste pagamento: ${formatBRL(available)}`}
                                </p>
                            )}
                            {valueHint && <HintBadge hint={valueHint} />}
                            <p className="text-xs text-gray-500 mt-1">
                                {postSettlement
                                    ? <>Título já liquidado — o valor recebido não abate dívida</>
                                    : !payment
                                        ? <>Escolha o pagamento acima</>
                                        : null}
                            </p>
                            {/* ⚠️ Este aviso era VERMELHO e mandava escolher outro pagamento.
                                Descrevia um impedimento que deixou de existir: com a marcação
                                parcial, usar o que sobra de um pagamento já parcialmente
                                identificado é o caso normal — a marcação ia bem, e o texto dizia
                                que não deveria. Agora ele só CONTEXTUALIZA o valor disponível, e
                                quem recusa de fato é a linha acima, quando o valor passa do saldo. */}
                            {partiallyMarked && !exceedsPayment && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Este pagamento já tem {formatBRL(payment!.markedAmount || 0)}{' '}
                                    com destino em outras faturas — o que sobra dele é o limite
                                    deste campo.
                                </p>
                            )}
                        </div>

                        {/* ── Atributos da COBRANÇA ──────────────────────────────────────
                            Vieram do registro do pagamento (AF-3671 step 2). Aqui é o único ponto
                            do fluxo onde se sabe QUAL cobrança o dinheiro paga — no passo 1 o
                            dinheiro entra sem fatura nenhuma. */}
                        <div className="grid grid-cols-2 gap-3 items-center">
                            <div>
                                <label className={LABEL} htmlFor="fonte-pagamento">
                                    Fonte do Pagamento
                                </label>
                                <select id="fonte-pagamento" className={SOURCE_SELECT} value={paymentSource}
                                    onChange={e => {
                                        setPaymentSource(e.target.value);
                                        // desmarca ao trocar para uma fonte que não admite aval:
                                        // deixar marcado enviaria um `true` que ela não comporta
                                        if (!allowsGuarantorRefund(sources, e.target.value)) {
                                            setGuarantorRefund(false);
                                        }
                                    }}>
                                    {/* sem "Selecione": há default para toda natureza de título
                                        (o `default_if` do seed cobre as três), então a opção vazia
                                        nunca seria a resposta certa — só um passo a mais */}
                                    {sources.map(src => (
                                        <option key={src.alias} value={src.alias}>{src.name}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Quem pagou esta fatura. Fica na cobrança, não no pagamento.
                                </p>
                            </div>
                            <div className="flex flex-col justify-start">
                                <label className={`inline-flex items-center gap-2 text-sm ${refundAllowed ? '' : 'opacity-50 cursor-not-allowed'}`}
                                    title={refundAllowed ? undefined
                                        : 'Só a fonte de pagamento que admite devolução habilita este campo'}>
                                    <input type="checkbox" checked={guarantorRefund}
                                        disabled={!refundAllowed}
                                        onChange={e => setGuarantorRefund(e.target.checked)} />
                                    Aval a Devolver
                                </label>
                            </div>
                        </div>

                        <div>
                            <label className={LABEL} htmlFor="obs-marcacao">Observações</label>
                            {(invoice.details?.paymentNotesList || []).length > 0 && (
                                <div className="mb-2">
                                    <NotesList notes={invoice.details.paymentNotesList}
                                        label="Anotações de pagamento anteriores" />
                                </div>
                            )}
                            <textarea id="obs-marcacao" className={INPUT} rows={2} value={notes}
                                placeholder="Referência bancária, NF, etc."
                                onChange={e => setNotes(e.target.value)} />
                            <p className="text-xs text-gray-500 mt-1">
                                Fica na sinalização e, na aprovação, é somado às anotações de pagamento da fatura.
                            </p>
                        </div>

                        {!reusesOriginal && !residualShown && replaceNotice}
                        {residualShown && (
                            <div data-testid="new-invoice-row" className="grid grid-cols-2 gap-3 items-start">
                                {replaceNotice}
                                <ResidualInvoicesField
                                    value={residualInvoices || []}
                                    onChange={setResidualConfigured}
                                    previousInvoice={invoice}
                                    charge={charge}
                                    balance={residualBalance}
                                    partialDenominator={partialDenominator}
                                />
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-3 border-t dark:border-slate-600 flex justify-end gap-2">
                        <button onClick={onClose} disabled={submitting}
                            className="px-4 py-2 text-sm bg-gray-200 dark:bg-slate-600 rounded hover:bg-gray-300">
                            Cancelar
                        </button>
                        {/* âmbar, a cor de "aguardando alguém" nas telas de pagamento — e não o
                            verde de liquidado, que é o que esta ação justamente NÃO faz */}
                        <button onClick={handleSubmit} disabled={!podeEnviar}
                            className="px-4 py-2 text-sm text-white rounded disabled:opacity-60 bg-amber-500 hover:bg-amber-600">
                            {submitting ? ui.submittingLabel : 'Sinalizar Pagamento'}
                        </button>
                    </div>
                </div>
            </div>

            {/* z acima do próprio modal (que é z-[60]): sem isso o dialog de informações abria
                ATRÁS e o usuário só via o overlay escurecer, sem entender o que aconteceu */}
            {infoPayment && (
                <PaymentInfoModal payment={infoPayment} zIndexClass="z-[70]"
                    onClose={() => setInfoPayment(null)} />
            )}
        </>
    );
};

export default MarkInvoicePaymentModal;
